// @flow

import React from 'react';
import Relay from 'react-relay/classic';
import { second, seconds } from 'metrick/duration';
import throttle from 'throttleit';

import Panel from 'app/components/shared/Panel';
import SearchField from 'app/components/shared/SearchField';
import ShowMoreFooter from 'app/components/shared/ShowMoreFooter';
import Spinner from 'app/components/shared/Spinner';
import { formatNumber } from 'app/lib/number';

import CableStore from 'app/stores/CableStore';

import AgentRow from './agent/Index/row';

const PAGE_SIZE = 50;

type Props = {
  organization: {
    allAgents: ?{
      count: number
    },
    agents: ?{
      count: number,
      edges: Array<Object>
    }
  },
  relay: Object
};

type State = {
  loading: boolean,
  searchingRemotely: boolean,
  searchingRemotelyIsSlow: boolean,
  localSearchQuery: ?Array<string>,
  defaultQuery: ?string
};

class ClusterQueueAgents extends React.PureComponent<Props, State> {
  agentListRefreshTimeout: TimeoutID;
  remoteSearchIsSlowTimeout: TimeoutID;

  state = {
    loading: false,
    searchingRemotely: false,
    searchingRemotelyIsSlow: false,
    localSearchQuery: null,
    defaultQuery: null
  };

  componentDidMount() {
    CableStore.on('organization_stats:change', this.fetchUpdatedData);
    this.startTimeout();
  }

  componentWillUnmount() {
    CableStore.off('organization_stats:change', this.fetchUpdatedData);
    clearTimeout(this.agentListRefreshTimeout);
  }

  // We refresh the data under 2 circumstances. The refresh happens on what
  // ever happens first.
  //
  // 1. We receive an `organization_stats:change` push event
  // 2. 10 seconds have passed
  startTimeout = () => {
    this.agentListRefreshTimeout = setTimeout(
      this.fetchUpdatedData,
      seconds.bind(10)
    );
  };

  // Throttle the `fetchUpdatedData` function so we don't ever reload the
  // entire list more than once every 3 seconds
  fetchUpdatedData = throttle(
    () => {
      this.props.relay.forceFetch(
        true,
        (readyState) => {
          if (readyState.done) {
            // Start a timeout that will cause the data to be refreshed again
            // in a few seconds time
            this.startTimeout();
          }
        }
      );
    },
    seconds.bind(3)
  );

  render() {
    // grab (potentially) filtered agent list here, as we need it in several places
    const agents = this.getRelevantAgents();

    return (
      <Panel>
        <Panel.Row>
          <div className="flex flex-wrap items-center">
            <h2 className="flex-auto h4 pr3 semi-bold">Agents</h2>
            <SearchField
              placeholder="Filter agents…"
              className="flex-auto"
              onChange={this.handleSearch}
              searching={this.state.searchingRemotelyIsSlow}
              defaultValue={this.state.defaultQuery}
            />
          </div>
        </Panel.Row>
        {this.renderSearchInfo(agents)}
        {this.renderAgentList(agents)}
        <ShowMoreFooter
          connection={this.props.organization.agents}
          label="agents"
          loading={this.state.loading}
          searching={!!this.state.localSearchQuery}
          onShowMore={this.handleShowMoreAgents}
        />
      </Panel>
    );
  }

  getRelevantAgents() {
    const { organization: { agents: organizationAgents } } = this.props;

    if (!organizationAgents) {
      return null;
    }

    const { edges: agents } = organizationAgents;
    const { localSearchQuery } = this.state;

    if (!localSearchQuery) {
      return agents;
    }

    // Parse the search. We only want to do this once, outside of the agent
    // loop, in case they have lots of agents
    const queries = localSearchQuery.map((string) => {
      const [key, value] = string.split('=');

      return {
        string: string.toLowerCase(),
        metaDataKey: key,
        metaDataValue: value // may be undefined
      };
    });

    const anyQueryMatchesName = (agent) => {
      const lowercaseName = agent.name.toLowerCase();

      return queries.some((query) => lowercaseName.indexOf(query.string) !== -1);
    };

    const anyQueryMatchesMetaData = (agent) => {
      return agent.metaData.some((metaDataKeyValue) => {
        const [key, value] = metaDataKeyValue.split('=');

        return queries.some((query) => {
          // Simple string match
          //
          // 'mo' matches 'moo=true'
          // 'tru' matches 'moo=true'
          // 'bark' does not match 'moo=true'
          if (!query.metaDataValue && key.indexOf(query.metaDataKey) !== -1 || value.indexOf(query.metaDataKey) !== -1) {
            return true;
          }

          // Wildcard matching
          //
          // 'moo=*' matches 'moo=true'
          // 'moo=*' doesn't match 'bark=true'
          if (query.metaDataKey === key && query.metaDataValue === '*') {
            return true;
          }

          // Key=Value matching
          //
          // 'moo=tr' matches 'moo=true'
          // 'moo=true' matches 'moo=true'
          // 'moo=rue' doesn't match 'moo=true'
          // 'moo=false' doesn't match 'moo=true'
          if (query.metaDataKey === key && value.indexOf(query.metaDataValue) === 0) {
            return true;
          }
        });
      });
    };

    return agents.filter(({ node: agent }) => {
      return anyQueryMatchesName(agent) || anyQueryMatchesMetaData(agent);
    });
  }

  renderSearchInfo(relevantAgents) {
    const { organization: { agents }, relay: { variables: { search: remoteSearchQuery } } } = this.props;
    const { localSearchQuery } = this.state;

    if ((localSearchQuery && relevantAgents) || remoteSearchQuery && agents) {
      return (
        <div className="bg-silver semi-bold py2 px3">
          <small className="dark-gray">
            {formatNumber(localSearchQuery ? (relevantAgents ? relevantAgents.length : 0) : (agents ? agents.count : 0))} matching agents
          </small>
        </div>
      );
    }
  }

  renderAgentList(relevantAgents) {
    const { relay: { variables: { search: remoteSearchQuery } } } = this.props;
    const { localSearchQuery } = this.state;

    const isContextFiltered = !!(remoteSearchQuery || localSearchQuery);

    if (!relevantAgents) {
      return (
        <Panel.Section className="center">
          <Spinner />
        </Panel.Section>
      );
    } else if (relevantAgents.length > 0) {
      return relevantAgents.map(({ node: agent }) => <AgentRow key={agent.id} agent={agent} showClusterBadge={false} />);
    } else if (!isContextFiltered) {
      return (
        <Panel.Section className="dark-gray">
          No agents connected
        </Panel.Section>
      );
    }
  }

  get useLocalSearch() {
    return this.props.organization.agents &&
      (this.props.organization.allAgents && this.props.organization.allAgents.count <= PAGE_SIZE) &&
      this.props.relay.variables.clusterQueue === null;
  }

  get useRemoteSearch() {
    return !this.useLocalSearch;
  }

  handleSearch = (query) => {
    if (this.useLocalSearch) {
      return this.handleLocalSearch(query);
    }

    return this.handleRemoteSearch(query);
  };

  handleLocalSearch = (query) => {
    // if there's a remote search active we make doubly sure to
    // reset it this shouldn't happen but stranger things have!
    const shouldResetSearch = !!this.props.relay.variables.search;

    // remove leading and trailing whitespace
    query = query.trim();

    const newState = Object.assign({}, this.state, {
      localSearchQuery: query ? query.split(/\s+/g) : null
    });

    if (shouldResetSearch) {
      newState.searchingRemotely = true;
    }

    this.setState(newState);

    if (shouldResetSearch) {
      this.props.relay.setVariables(
        {
          search: null
        },
        (readyState) => {
          if (readyState.done) {
            this.setState({ searchingRemotely: false });
          }
        }
      );
    }
  };

  handleRemoteSearch = (query) => {
    this.setState({ localSearchQuery: null, searchingRemotely: true });

    if (this.remoteSearchIsSlowTimeout) {
      clearTimeout(this.remoteSearchIsSlowTimeout);
    }

    this.remoteSearchIsSlowTimeout = setTimeout(() => {
      this.setState({ searchingRemotelyIsSlow: true });
    }, second.bind(1));

    this.props.relay.forceFetch(
      {
        search: query
      },
      (readyState) => {
        if (readyState.done) {
          if (this.remoteSearchIsSlowTimeout) {
            clearTimeout(this.remoteSearchIsSlowTimeout);
          }
          this.setState({
            searchingRemotely: false,
            searchingRemotelyIsSlow: false
          });
        }
      }
    );
  };

  handleShowMoreAgents = () => {
    this.setState({ loading: true });

    this.props.relay.setVariables(
      {
        pageSize: this.props.relay.variables.pageSize + PAGE_SIZE
      },
      (readyState) => {
        if (readyState.done) {
          this.setState({ loading: false });
        }
      }
    );
  };
}

export default Relay.createContainer(ClusterQueueAgents, {
  initialVariables: {
    search: null,
    clusterQueue: null,
    pageSize: PAGE_SIZE
  },

  fragments: {
    organization: () => Relay.QL`
      fragment on Organization {
        allAgents: agents {
          count
        }
        agents(first: $pageSize, search: $search, clusterQueue: $clusterQueue) {
          ${ShowMoreFooter.getFragment('connection')}
          count
          edges {
            node {
              id
              metaData
              name
              ${AgentRow.getFragment('agent')}
            }
          }
        }
      }
    `
  }
});
